import { useEffect } from "react";
import { useAccount, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { useIsApprovedForAll } from "../../hooks";
import { useTransactionStore } from "../../stores/transactionStore";
import { contractConfigs } from "../../web3/contracts";
import { LoadingButton } from "../shared/LoadingButton";

interface ApprovalStepProps {
	selectedShipId: bigint | null;
	onLaunch: () => void;
	isLaunching: boolean;
}

export function ApprovalStep({
	selectedShipId,
	onLaunch,
	isLaunching,
}: ApprovalStepProps) {
	const { address } = useAccount();
	const addToast = useTransactionStore((s) => s.addToast);

	// Ship NFT approval (setApprovalForAll)
	const { isApproved: isShipApproved, refetch: refetchShipApproval } = useIsApprovedForAll(
		address,
		contractConfigs.preTgeFarming.address,
	);

	const {
		writeContract: approveShipWrite,
		data: shipApproveHash,
		isPending: isShipApprovePending,
	} = useWriteContract();
	const { isLoading: isShipApproveConfirming, isSuccess: shipApproveSuccess } =
		useWaitForTransactionReceipt({ hash: shipApproveHash });

	const isApprovingShip = isShipApprovePending || isShipApproveConfirming;

	function handleApproveShip(): void {
		approveShipWrite({
			...contractConfigs.shipNFT,
			functionName: "setApprovalForAll",
			args: [contractConfigs.preTgeFarming.address, true],
		});
	}

	const canLaunch = isShipApproved && selectedShipId !== null;

	// Toast + refetch on Ship approval success
	useEffect(() => {
		if (shipApproveSuccess) {
			addToast({
				type: "success",
				title: "Ships Approved",
				message: "Ship NFTs approved for farming contract.",
			});
			refetchShipApproval();
		}
	}, [shipApproveSuccess, addToast, refetchShipApproval]);

	return (
		<div className="approval-step">
			<h3 className="approval-step__title">Approve & Launch</h3>

			{/* Ship Approval */}
			<div
				className={`approval-step__item ${isShipApproved ? "approval-step__item--approved" : ""}`}
			>
				<span className="approval-step__icon">{isShipApproved ? "✓" : "1"}</span>
				<div className="approval-step__label">
					<span className="approval-step__name">Ship NFT</span>
					<span className="approval-step__status">
						{isShipApproved ? "Approved" : "Approve ships for farming"}
					</span>
				</div>
				{!isShipApproved && (
					<LoadingButton onClick={handleApproveShip} loading={isApprovingShip} variant="secondary">
						Approve Ships
					</LoadingButton>
				)}
			</div>

			{/* Launch Button */}
			<div className="approval-step__launch">
				<LoadingButton onClick={onLaunch} loading={isLaunching} disabled={!canLaunch}>
					Launch Mission
				</LoadingButton>
			</div>
		</div>
	);
}
